// One AssemblyAI v3 streaming session, opened straight from the browser with a temporary
// token. Binary PCM16 frames go up; `Turn` messages come down.

import { voiceLog, wordCount } from './log.js';

const URL_BASE = 'wss://streaming.assemblyai.com/v3/ws';
const SAMPLE_RATE = 16000;
// The docs list `speech_model` as a required parameter; this is the current default model.
export const SPEECH_MODEL = 'universal-3-6-pro';
// Audio sent before the socket is open is kept, up to about two seconds, oldest dropped.
const MAX_BUFFERED_SAMPLES = SAMPLE_RATE * 2;

export type TranscriptionOptions = {
  token: string;
  minTurnSilenceMs: number;
  maxTurnSilenceMs: number;
  /** Text of a turn still in progress. `turnOrder` is the vendor's number for the turn. */
  onPartial: (text: string, turnOrder: number) => void;
  /** A finished turn (formatted when the vendor formats). */
  onFinal: (text: string, turnOrder: number) => void;
  onSpeechStarted: () => void;
  /** Seconds the vendor connection was open; called at most once, and only if it opened. */
  onUsage: (seconds: number) => void;
  /** Only for a close the caller did not ask for. */
  onClosed: (reason: 'dropped') => void;
};

export type Transcription = {
  send: (frame: Int16Array) => void;
  forceEndpoint: () => void;
  terminate: () => void;
};

export function openTranscription(options: TranscriptionOptions): Transcription {
  const query = new URLSearchParams({
    token: options.token,
    sample_rate: String(SAMPLE_RATE),
    encoding: 'pcm_s16le',
    speech_model: SPEECH_MODEL,
    min_turn_silence: String(options.minTurnSilenceMs),
    max_turn_silence: String(options.maxTurnSilenceMs),
  });
  const socket = new WebSocket(`${URL_BASE}?${query.toString()}`);
  socket.binaryType = 'arraybuffer';
  let ending = false;
  let openedAt: number | null = null;
  let reported = false;
  const reportUsage = (): void => {
    if (reported || openedAt === null) {
      return;
    }
    reported = true;
    options.onUsage((performance.now() - openedAt) / 1000);
  };
  let buffered: Int16Array[] = [];
  let bufferedSamples = 0;

  const flush = (): void => {
    for (const frame of buffered) {
      socket.send(frame);
    }
    buffered = [];
    bufferedSamples = 0;
  };

  socket.onopen = () => {
    openedAt = performance.now();
    voiceLog('stt socket open', { bufferedFrames: buffered.length });
    flush();
  };
  socket.onmessage = (event: MessageEvent<unknown>) => {
    if (ending || typeof event.data !== 'string') {
      return;
    }
    let message: unknown;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    if (message === null || typeof message !== 'object') {
      return;
    }
    const raw = message as Record<string, unknown>;
    const kind = typeof raw['type'] === 'string' ? raw['type'] : 'unknown';
    if (kind !== 'Turn') {
      voiceLog('stt message', { type: kind });
    }
    if (raw['type'] === 'SpeechStarted') {
      options.onSpeechStarted();
      return;
    }
    if (raw['type'] !== 'Turn' || typeof raw['transcript'] !== 'string') {
      return;
    }
    const text = raw['transcript'];
    const order = typeof raw['turn_order'] === 'number' ? raw['turn_order'] : 0;
    // An end of turn whose text the vendor says is not yet formatted waits for the
    // formatted one; the newer models always format, so `turn_is_formatted` is then absent.
    const done = raw['end_of_turn'] === true && raw['turn_is_formatted'] !== false;
    voiceLog('stt message', {
      type: 'Turn',
      turn_order: typeof raw['turn_order'] === 'number' ? raw['turn_order'] : null,
      end_of_turn: raw['end_of_turn'] === true,
      turn_is_formatted:
        typeof raw['turn_is_formatted'] === 'boolean' ? raw['turn_is_formatted'] : null,
      words: wordCount(text),
      treatedAs: done ? 'final' : 'partial',
    });
    if (done) {
      options.onFinal(text, order);
    } else {
      options.onPartial(text, order);
    }
  };
  socket.onclose = (event: CloseEvent) => {
    voiceLog('stt socket close', {
      code: event.code,
      reason: event.reason,
      asked: ending,
    });
    reportUsage();
    if (!ending) {
      ending = true;
      options.onClosed('dropped');
    }
  };
  socket.onerror = () => {
    voiceLog('stt socket error');
    // `onclose` follows and reports it.
  };

  return {
    send(frame) {
      if (ending) {
        return;
      }
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(frame);
        return;
      }
      if (socket.readyState !== WebSocket.CONNECTING) {
        return;
      }
      buffered.push(frame);
      bufferedSamples += frame.length;
      while (bufferedSamples > MAX_BUFFERED_SAMPLES && buffered.length > 1) {
        bufferedSamples -= (buffered.shift() as Int16Array).length;
      }
    },
    forceEndpoint() {
      if (!ending && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ForceEndpoint' }));
      }
    },
    terminate() {
      if (ending) {
        return;
      }
      reportUsage();
      ending = true;
      buffered = [];
      if (socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'Terminate' }));
        } catch {
          // Closing below.
        }
        // Give the vendor a moment to acknowledge, then close regardless.
        setTimeout(() => socket.close(), 500);
      } else {
        socket.close();
      }
    },
  };
}
